import { useEffect, useState } from "react";
import { api, fileUrl, API } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Mail, MessageSquare, Hammer, FileDown, AlertTriangle } from "lucide-react";

const CHANNELS = [
  { key: "email", label: "Email (supervisor)", icon: Mail, color: "#1a1a1a", note: "MOCKED — no real email is sent." },
  { key: "msteams", label: "MS Teams webhook", icon: MessageSquare, color: "#1a1a1a", note: "MOCKED — no real webhook called." },
  { key: "inapp_workorder", label: "In-app work order", icon: Hammer, color: "#ff4500", note: "Delivered to recipient team's Work Orders inbox." },
  { key: "pdf", label: "Generate PDF", icon: FileDown, color: "#1a1a1a", note: "Server-rendered PDF; download below after dispatch." },
];

export default function DistributionDialog({ open, onClose, run }) {
  const [teams, setTeams] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(null); // null = all items
  const [channels, setChannels] = useState({ pdf: true });
  const [recipientEmail, setRecipientEmail] = useState("supervisor@acme-mfg.com");
  const [recipientTeam, setRecipientTeam] = useState("");
  const [requestType, setRequestType] = useState("work_order");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
      setSelectedIdx(null);
      setChannels({ pdf: true });
      setTitle(run?.template_name || "");
      setNotes("");
      setResult(null);
    }
  }, [open, run]);

  if (!run) return null;

  const toggleChannel = (k) => setChannels({ ...channels, [k]: !channels[k] });
  const toggleItem = (i) => {
    if (selectedIdx === null) {
      // start a selection with all items first
      setSelectedIdx(run.items.map((_, idx) => idx).filter((x) => x !== i));
    } else if (selectedIdx.includes(i)) {
      setSelectedIdx(selectedIdx.filter((x) => x !== i));
    } else {
      setSelectedIdx([...selectedIdx, i]);
    }
  };

  const send = async () => {
    const chs = Object.keys(channels).filter((k) => channels[k]);
    if (chs.length === 0) return toast.error("Select at least one channel");
    setBusy(true);
    try {
      const r = await api.post("/distribute", {
        run_id: run.id,
        selected_indices: selectedIdx,
        channels: chs,
        recipient_email: recipientEmail || null,
        recipient_team_id: recipientTeam || null,
        request_type: requestType,
        title, notes,
      });
      setResult(r.data);
      toast.success("Distributed");
    } catch (e) {
      toast.error("Distribution failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = (fileId) => {
    const token = localStorage.getItem("opscmd_token");
    window.open(`${API}/files/${fileId}?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sharp max-w-3xl border-[#1a1a1a]/30 max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase tracking-wider">Distribute audit</DialogTitle>
        </DialogHeader>

        {!result && (
          <div className="space-y-5">
            {/* Step 1: items */}
            <section>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// 1. Select items</div>
              <div className="flex gap-2 mb-2">
                <button data-testid="select-all-items" onClick={() => setSelectedIdx(null)}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold ${selectedIdx === null ? "bg-[#1a1a1a] text-white" : "border border-[#1a1a1a]/30 text-[#1a1a1a]"}`}>
                  All items
                </button>
                <button data-testid="select-some-items" onClick={() => setSelectedIdx(selectedIdx === null ? run.items.map((_, i) => i) : selectedIdx)}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold ${selectedIdx !== null ? "bg-[#1a1a1a] text-white" : "border border-[#1a1a1a]/30 text-[#1a1a1a]"}`}>
                  Select specific
                </button>
              </div>
              {selectedIdx !== null && (
                <ul className="border border-[#1a1a1a]/15 max-h-48 overflow-auto">
                  {run.items.map((it, i) => (
                    <li key={i} className="flex items-center gap-3 px-3 py-2 border-b border-[#1a1a1a]/10 last:border-b-0">
                      <input data-testid={`dist-item-${i}`} type="checkbox" checked={selectedIdx.includes(i)} onChange={() => toggleItem(i)} className="accent-[#ff4500]" />
                      <div className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${it.status === "fail" ? "bg-[#ff4500] text-white" : it.status === "pass" ? "bg-[#1a8754] text-white" : "bg-[#1a1a1a]/20 text-[#1a1a1a]"}`}>
                        {it.status || "—"}
                      </div>
                      <div className="text-sm flex-1">{it.title}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Step 2: channels */}
            <section>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// 2. Channels</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {CHANNELS.map((c) => {
                  const Icon = c.icon;
                  const active = !!channels[c.key];
                  return (
                    <button key={c.key} data-testid={`channel-${c.key}`} onClick={() => toggleChannel(c.key)}
                      className={`text-left p-3 border ${active ? "border-[#1a1a1a] bg-[#f0ede6]" : "border-[#1a1a1a]/20 bg-white"} transition-colors duration-150`}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: c.color }} />
                        <div className="font-display font-bold text-sm">{c.label}</div>
                        <input type="checkbox" readOnly checked={active} className="ml-auto accent-[#ff4500]" />
                      </div>
                      <div className="text-[11px] text-[#1a1a1a]/70 mt-1">{c.note}</div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Step 3: details */}
            <section>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-2">// 3. Details</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/70 mb-1.5 font-mono">Title</label>
                  <input data-testid="dist-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
                    className="w-full bg-white border border-[#1a1a1a]/30 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/70 mb-1.5 font-mono">Request type</label>
                  <select data-testid="dist-type-input" value={requestType} onChange={(e) => setRequestType(e.target.value)}
                    className="w-full bg-white border border-[#1a1a1a]/30 px-3 py-2 text-sm">
                    <option value="work_order">Work order</option>
                    <option value="quality_issue">Quality issue</option>
                    <option value="info">FYI / info</option>
                  </select>
                </div>
                {channels.email && (
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/70 mb-1.5 font-mono">Supervisor email</label>
                    <input data-testid="dist-email-input" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
                      className="w-full bg-white border border-[#1a1a1a]/30 px-3 py-2 text-sm" />
                  </div>
                )}
                {channels.inapp_workorder && (
                  <div>
                    <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/70 mb-1.5 font-mono">Recipient team</label>
                    <select data-testid="dist-team-input" value={recipientTeam} onChange={(e) => setRecipientTeam(e.target.value)}
                      className="w-full bg-white border border-[#1a1a1a]/30 px-3 py-2 text-sm">
                      <option value="">Select team…</option>
                      {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/70 mb-1.5 font-mono">Notes / message</label>
                  <textarea data-testid="dist-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
                    className="w-full bg-white border border-[#1a1a1a]/30 px-3 py-2 text-sm h-20" />
                </div>
              </div>
            </section>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Distribution result</div>
            <div className="border border-[#1a1a1a]/20 divide-y divide-[#1a1a1a]/10">
              {Object.entries(result.channels).map(([k, v]) => (
                <div key={k} className="px-3 py-2 flex items-center justify-between gap-2">
                  <div className="text-sm font-display font-bold uppercase tracking-wider">{k}</div>
                  <div className="flex items-center gap-2">
                    {(v.status || "").includes("mocked") && (
                      <span className="inline-flex items-center gap-1 bg-[#ff4500] text-white text-[10px] px-2 py-0.5 uppercase tracking-wider font-bold">
                        <AlertTriangle className="w-3 h-3" /> MOCKED
                      </span>
                    )}
                    <div className="text-[11px] font-mono text-[#1a1a1a]/70">{v.status}</div>
                    {k === "pdf" && v.file_id && (
                      <button data-testid="download-pdf-button" onClick={() => downloadPdf(v.file_id)}
                        className="bg-[#1a1a1a] text-white px-3 py-1 text-[10px] uppercase tracking-wider font-bold hover:bg-[#000000]">Download</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {result.pdf_file_id && !result.channels.pdf && (
              <button data-testid="download-pdf-alt-button" onClick={() => downloadPdf(result.pdf_file_id)}
                className="inline-flex items-center gap-2 border border-[#1a1a1a] text-[#1a1a1a] px-3 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#1a1a1a] hover:text-white transition-colors duration-150">
                <FileDown className="w-3.5 h-3.5" /> Download report PDF
              </button>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <button data-testid="dispatch-button" onClick={send} disabled={busy}
              className="bg-[#ff4500] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#cc3500] disabled:opacity-60">
              {busy ? "Dispatching…" : "Dispatch"}
            </button>
          ) : (
            <button data-testid="dist-close-button" onClick={onClose}
              className="border border-[#1a1a1a] text-[#1a1a1a] px-4 py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#1a1a1a] hover:text-white transition-colors duration-150">
              Close
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
